/**
 * THE FIRM ADMIN'S AGREEMENT GROUP, `cutoverAgreementViews` at
 * `/api/bms/cutover-agreement-views`, on the internal session lane only
 * (cutover plan ruling 28; note 01 E.1, E.2; research 01 §2.3.6, §2.3.7;
 * api-conventions §7).
 *
 * BROWSER-SAFE. The settings slice and the root banner import THIS module to
 * build their transport, never the leaf's barrel, which publishes a
 * repository that reaches `@effect/sql-pg`. Kit imports are relative for the
 * same reason.
 *
 * INTERNAL ONLY, NEVER PUBLISHED. An agreement is the firm administrator's
 * signed act in the app, never a firm key's: there is no `/api/v1` twin. THIS
 * BUILD ADDS NO RPC GROUP (CLAUDE.md rule 3, BMS exception).
 *
 * FIVE ENDPOINTS. The status read is cheap (the migration row and the latest
 * agreement's counts, no matcher), because the root banner asks it for every
 * administrator of a legacy firm. The proposal the page resolves is the
 * matcher's own read (`cutoverMatcherViews`), not repeated here. The write
 * agrees, and answers the new status.
 *
 * THE MATCH DRAFT (decision 041): the administrator's answers saved as they
 * are made, so the matches can be worked over several sittings. One read of
 * the whole draft, one write per answer (insert or replace the legacy
 * client's answer) and one clear. Nothing moves from the draft: `agree` still
 * carries the resolution the page builds over it.
 *
 * THE FIRM IS IN THE PATH and must be the caller's own: another firm's id
 * answers not found, as if it did not exist. The caller must be the firm's
 * administrator; anyone else is refused 403 before anything is read.
 */
import { HttpApi, HttpApiEndpoint, HttpApiGroup, OpenApi } from "@effect/platform"
import { Schema as S } from "effect"

// RELATIVE, not the public-api barrel: see the header.
import { ApiConflictWire } from "../../../../public-api/domain/public-api-error"
import { withBmsErrors } from "../../../shared/presentation/errors"
import { BmsSessionMiddleware } from "../../../shared/presentation/session-middleware"
import { AgreePayload, AgreementStatus } from "../domain/agreement"
import { BookClientHits, BookSearchQuery, LegacyClientHits } from "../../../shared/domain/cutover-book-search"
import {
  ClearGroupAnswersPayload,
  ClearedGroupAnswers,
  ClearedMatchChoice,
  MatchDraft,
  MatchDraftRow,
  PrefillReport,
  SaveMatchChoicePayload,
  SaveMatchChoicesPayload,
  SavedMatchChoices,
} from "../domain/match-draft"

export const AgreementPath = S.Struct({ organizationId: S.UUID })

export const getAgreementStatusEndpoint = HttpApiEndpoint.get("getAgreementStatus", "/organizations/:organizationId/status")
  .setPath(AgreementPath)
  .addSuccess(AgreementStatus)
  .annotate(OpenApi.Identifier, "getAgreementStatus")
  .annotate(OpenApi.Summary, "Read where the firm's move to Cova BMS stands")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only. Whether support has requested the move, the firm's state, its latest agreement as counts, and whether an agreement can be given now.",
  )

export const agreeEndpoint = HttpApiEndpoint.post("agree", "/organizations/:organizationId/agreement")
  .setPath(AgreementPath)
  .setPayload(AgreePayload)
  .addSuccess(AgreementStatus)
  .addError(ApiConflictWire)
  .annotate(OpenApi.Identifier, "agreeToMove")
  .annotate(OpenApi.Summary, "Agree to move the firm's clients to Cova BMS")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only. Records the agreement, the scope and one decision per client in the firm's old client list, checked against the current match proposal. Refused with a conflict when the firm already agreed or its move has started.",
  )

export const MatchDraftPath = S.Struct({ organizationId: S.UUID, legacyId: S.UUID })

export const getMatchDraftEndpoint = HttpApiEndpoint.get("getMatchDraft", "/organizations/:organizationId/draft")
  .setPath(AgreementPath)
  .addSuccess(MatchDraft)
  .annotate(OpenApi.Identifier, "getMatchDraft")
  .annotate(OpenApi.Summary, "Read the firm administrator's saved client match answers")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only. One saved answer per legacy client answered so far, with who answered and when, so the matches can be worked over several sittings.",
  )

export const saveMatchChoiceEndpoint = HttpApiEndpoint.post("saveMatchChoice", "/organizations/:organizationId/draft/:legacyId")
  .setPath(MatchDraftPath)
  .setPayload(SaveMatchChoicePayload)
  .addSuccess(MatchDraftRow)
  .annotate(OpenApi.Identifier, "saveMatchChoice")
  .annotate(OpenApi.Summary, "Save the administrator's answer for one client match")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only, while the firm can take an agreement. Saves join, keep apart or decide later for one legacy client, replacing its last answer. Nothing moves until the administrator agrees.",
  )

export const clearMatchChoiceEndpoint = HttpApiEndpoint.post("clearMatchChoice", "/organizations/:organizationId/draft/:legacyId/clear")
  .setPath(MatchDraftPath)
  .addSuccess(ClearedMatchChoice)
  .annotate(OpenApi.Identifier, "clearMatchChoice")
  .annotate(OpenApi.Summary, "Clear the administrator's saved answer for one client match")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only, while the firm can take an agreement. The legacy client reads as never answered again.",
  )

// ============================================================================
// THE ONBOARDING'S BATCH, PREFILL AND SEARCHES (build plan 2026-11-03-002, Task 3)
// ============================================================================

export const saveMatchChoicesEndpoint = HttpApiEndpoint.post("saveMatchChoices", "/organizations/:organizationId/drafts")
  .setPath(AgreementPath)
  .setPayload(SaveMatchChoicesPayload)
  .addSuccess(SavedMatchChoices)
  .annotate(OpenApi.Identifier, "saveMatchChoices")
  .annotate(OpenApi.Summary, "Save many client match answers at once")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only, while the firm can take an agreement. A group's answer or an answer to a filtered set, in one transaction. An answer one by one is never replaced by a batch, and an answer carried from the last agreement or a filter only when `replace` is set.",
  )

export const prefillFromLastAgreementEndpoint = HttpApiEndpoint.post(
  "prefillFromLastAgreement",
  "/organizations/:organizationId/drafts/from-last-agreement",
)
  .setPath(AgreementPath)
  .addSuccess(PrefillReport)
  .annotate(OpenApi.Identifier, "prefillFromLastAgreement")
  .annotate(OpenApi.Summary, "Start from the firm's last agreement")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only, while the firm can take an agreement. Saves the firm's last agreement's answer for every client that has no saved answer and that Cova does not lock. Never touches a saved answer.",
  )

export const clearGroupAnswersEndpoint = HttpApiEndpoint.post("clearGroupAnswers", "/organizations/:organizationId/drafts/clear")
  .setPath(AgreementPath)
  .setPayload(ClearGroupAnswersPayload)
  .addSuccess(ClearedGroupAnswers)
  .annotate(OpenApi.Identifier, "clearGroupAnswers")
  .annotate(OpenApi.Summary, "Take back one group's own client match answers")
  .annotate(
    OpenApi.Description,
    "The firm's administrator only, while the firm can take an agreement. Removes, in one transaction, the answers a group card gave among the named legacy clients. An answer given one by one, to a filtered set or carried from the last agreement is never removed.",
  )

export const searchBookClientsEndpoint = HttpApiEndpoint.get("searchBookClients", "/organizations/:organizationId/search/book")
  .setPath(AgreementPath)
  .setUrlParams(BookSearchQuery)
  .addSuccess(BookClientHits)
  .annotate(OpenApi.Identifier, "searchBookClients")
  .annotate(OpenApi.Summary, "Find a client in Cova CRM to join a legacy client to")
  .annotate(OpenApi.Description, "The firm's administrator only. By name, trading name, company name, Steadfast Apps code, email or ABN; twenty at most.")

export const searchLegacyClientsEndpoint = HttpApiEndpoint.get("searchLegacyClients", "/organizations/:organizationId/search/legacy")
  .setPath(AgreementPath)
  .setUrlParams(BookSearchQuery)
  .addSuccess(LegacyClientHits)
  .annotate(OpenApi.Identifier, "searchLegacyClients")
  .annotate(OpenApi.Summary, "Find another of the firm's legacy clients to keep together with")
  .annotate(OpenApi.Description, "The firm's administrator only. By name, company name, email or Steadfast Apps code; twenty at most.")

export const CutoverAgreementViewsGroup = withBmsErrors(
  HttpApiGroup.make("cutoverAgreementViews")
    .add(getAgreementStatusEndpoint)
    .add(agreeEndpoint)
    .add(getMatchDraftEndpoint)
    .add(saveMatchChoiceEndpoint)
    .add(clearMatchChoiceEndpoint)
    .add(saveMatchChoicesEndpoint)
    .add(prefillFromLastAgreementEndpoint)
    .add(clearGroupAnswersEndpoint)
    .add(searchBookClientsEndpoint)
    .add(searchLegacyClientsEndpoint),
)
  .middleware(BmsSessionMiddleware)
  .prefix("/api/bms/cutover-agreement-views")

export class CutoverAgreementViewsApi extends HttpApi.make("BmsCutoverAgreementViewsApi").add(CutoverAgreementViewsGroup) {}
